import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { PunchCard } from "@/components/PunchCard";
import { api, todayKL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Admin: all punches, filterable by staff, date range, review status and match
   result, plus CSV export (spec §9). */
export default function AdminPunchesPage() {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState(todayKL(-7));
  const [dateTo, setDateTo] = useState(todayKL(0));
  const [userId, setUserId] = useState("all");
  const [reviewStatus, setReviewStatus] = useState("all");
  const [matchResult, setMatchResult] = useState("all");
  const [punctuality, setPunctuality] = useState("all");

  const { data: usersData } = useQuery({
    queryKey: ["users", "", "all"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["admin-punches", dateFrom, dateTo, userId, reviewStatus, matchResult, punctuality],
    queryFn: async () => (await api.get("/punches", {
      params: {
        dateFrom, dateTo,
        userId: userId === "all" ? "" : userId,
        reviewStatus: reviewStatus === "all" ? "" : reviewStatus,
        matchResult: matchResult === "all" ? "" : matchResult,
        punctuality: punctuality === "all" ? "" : punctuality,
        pageSize: 100,
      },
    })).data,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/punches/${id}/review`, body)).data,
    onSuccess: () => {
      toast.success(t("done"));
      queryClient.invalidateQueries({ queryKey: ["admin-punches"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  // Deletion is ADMIN-ONLY — the backend enforces it too (403 for supervisors)
  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/punches/${id}`)).data,
    onSuccess: () => {
      toast.success(t("punchDeleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-punches"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const exportCsv = async () => {
    try {
      const res = await api.get("/punches/export/csv", {
        params: {
          dateFrom, dateTo,
          userId: userId === "all" ? "" : userId,
          punctuality: punctuality === "all" ? "" : punctuality,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rajinkerja_punches_${dateFrom}_${dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <AppShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{t("allPunches")}</h1>
          <Button onClick={exportCsv} data-testid="admin-export-csv-button" variant="outline">
            <Download className="mr-2 h-4 w-4" /> {t("exportCsv")}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="punches-date-from" className="h-9 w-auto" />
          <span className="text-muted-foreground">–</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="punches-date-to" className="h-9 w-auto" />
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="punches-staff-filter" className="h-9 w-44">
              <SelectValue placeholder={t("selectStaff")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStaff")}</SelectItem>
              {usersData?.items?.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.fullName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={reviewStatus} onValueChange={setReviewStatus}>
            <SelectTrigger data-testid="punches-status-filter" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="pending">{t("pending")}</SelectItem>
              <SelectItem value="approved">{t("approved")}</SelectItem>
              <SelectItem value="flagged">{t("flagged")}</SelectItem>
              <SelectItem value="overridden">{t("overridden")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={matchResult} onValueChange={setMatchResult}>
            <SelectTrigger data-testid="punches-match-filter" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allResults")}</SelectItem>
              <SelectItem value="pass">{t("matchPass")}</SelectItem>
              <SelectItem value="fail">{t("matchFail")}</SelectItem>
              <SelectItem value="no_face_detected">{t("noFace")}</SelectItem>
              <SelectItem value="unverified">{t("matchUnverified")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={punctuality} onValueChange={setPunctuality}>
            <SelectTrigger data-testid="punches-punctuality-filter" className="h-9 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allPunctuality")}</SelectItem>
              <SelectItem value="late">{t("lateClockIns")}</SelectItem>
              <SelectItem value="early">{t("earlyClockOuts")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-36 w-full rounded-xl" />)}
        {!isLoading && (data?.items || []).length === 0 && (
          <p data-testid="punches-empty" className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t("noResults")}
          </p>
        )}
        <div className="space-y-3">
          {data?.items?.map((p) => (
            <PunchCard
              key={p.id}
              punch={p}
              busy={reviewMutation.isPending || deleteMutation.isPending}
              onReview={(id, body) => reviewMutation.mutateAsync({ id, body })}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}
